import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useCart } from "@/contexts/CartContext";

// 5 portrait images — each has a custom objectPosition to frame the face correctly
const PORTRAITS = [
  {
    src: "/manus-storage/hero-portrait-1_4a073e01.png",
    name: "ARIA-7",
    role: "Executive AI Partner",
    dept: "Enterprise",
    deptId: "enterprise",
    employeeId: 69,
    color: "#00f5ff",
    objectPosition: "center 30%",
    skills: ["Strategic Planning", "Stakeholder Comms", "Decision Intel"],
  },
  {
    src: "/manus-storage/hero-portrait-2_a128c5e6.png",
    name: "LYRA-X",
    role: "Brand Architect",
    dept: "Marketing",
    deptId: "marketing",
    employeeId: 13,
    color: "#ff2d78",
    objectPosition: "center 25%",
    skills: ["Brand Strategy", "Content Systems", "Growth Ops"],
  },
  {
    src: "/manus-storage/hero-portrait-3_94547b3d.png",
    name: "MERIDIAN",
    role: "Operations Director",
    dept: "Operations",
    deptId: "operations",
    employeeId: 41,
    color: "#f5a623",
    objectPosition: "center 32%",
    skills: ["Process Design", "Team Coordination", "Analytics"],
  },
  {
    src: "/manus-storage/Gemini_Generated_Image_4eo46x4eo46x4eo4_4381c434.jpg",
    name: "NOVA-9",
    role: "Tech Intelligence Agent",
    dept: "Technology",
    deptId: "technology",
    employeeId: 5,
    color: "#a855f7",
    objectPosition: "center 40%",
    skills: ["Cyber Analysis", "Neural Ops", "System Breach"],
  },
  {
    src: "/manus-storage/Gemini_Generated_Image_d571aid571aid571_3e0f590e.jpg",
    name: "CIPHER",
    role: "Data Intelligence Lead",
    dept: "Data",
    deptId: "data",
    employeeId: 90,
    color: "#06ffa5",
    objectPosition: "center 40%",
    skills: ["Data Mining", "Predictive Intel", "Pattern Recon"],
  },
];

// ── Mobile Carousel (used by CyberHeader on small screens) ────────────────
function MobilePortraitCarousel() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [glitching, setGlitching] = useState(false);
  const [, navigate] = useLocation();
  const { openCart } = useCart();
  const touchStartX = useRef<number | null>(null);

  const goTo = (i: number) => {
    setGlitching(true);
    setTimeout(() => { setActiveIdx(i); setGlitching(false); }, 200);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitching(true);
      setTimeout(() => { setActiveIdx((prev) => (prev + 1) % PORTRAITS.length); setGlitching(false); }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const p = PORTRAITS[activeIdx];

  return (
    <div style={{ position: "relative", width: "100%", overflow: "hidden", borderRadius: 12, background: "rgba(4,8,20,0.95)" }}>
      {/* Portrait */}
      <div style={{ position: "relative", width: "100%", height: 280, overflow: "hidden" }}>
        <img
          src={p.src}
          alt={p.name}
          style={{
            width: "100%", height: "100%",
            objectFit: "cover",
            objectPosition: p.objectPosition ?? "center 10%",
            display: "block",
            filter: glitching ? "hue-rotate(90deg) saturate(3) brightness(1.5)" : "none",
            transition: "filter 0.25s ease",
          }}
          loading="lazy"
        />
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,245,255,0.02) 2px, rgba(0,245,255,0.02) 4px)",
        }} />
        <div style={{
          position: "absolute", left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, transparent, ${p.color}88, transparent)`,
          animation: "scanSweepV 3s linear infinite",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 120,
          background: "linear-gradient(to top, rgba(4,8,20,1) 0%, rgba(4,8,20,0.5) 60%, transparent 100%)",
          pointerEvents: "none",
        }} />
        {[
          { top: 10, left: 10, borderTop: `2px solid ${p.color}`, borderLeft: `2px solid ${p.color}` },
          { top: 10, right: 10, borderTop: `2px solid ${p.color}`, borderRight: `2px solid ${p.color}` },
          { bottom: 10, left: 10, borderBottom: `2px solid ${p.color}`, borderLeft: `2px solid ${p.color}` },
          { bottom: 10, right: 10, borderBottom: `2px solid ${p.color}`, borderRight: `2px solid ${p.color}` },
        ].map((s, i) => (
          <div key={i} style={{ position: "absolute", width: 18, height: 18, zIndex: 8, ...s }} />
        ))}
        <div style={{
          position: "absolute", top: 14, left: 14,
          display: "inline-flex", alignItems: "center", gap: 6,
          background: `${p.color}18`, border: `1px solid ${p.color}55`,
          borderRadius: 4, padding: "3px 10px",
        }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: p.color, boxShadow: `0 0 6px ${p.color}` }} />
          <span style={{ fontFamily: "\'Orbitron\', monospace", fontSize: 9, letterSpacing: 2, color: p.color, textTransform: "uppercase" }}>
            {p.dept} Division
          </span>
        </div>
      </div>
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ fontFamily: "\'Orbitron\', monospace", fontSize: 20, fontWeight: 900, color: "#fff", letterSpacing: 2 }}>{p.name}</div>
        <div style={{ fontFamily: "\'Rajdhani\', sans-serif", fontSize: 13, color: `${p.color}cc`, letterSpacing: 1.5, textTransform: "uppercase", marginTop: 2 }}>{p.role}</div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 8 }}>
          {p.skills.map((skill) => (
            <span key={skill} style={{
              fontFamily: "\'Rajdhani\', sans-serif", fontSize: 10, letterSpacing: 1,
              color: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "3px 8px",
            }}>{skill}</span>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14 }}>
          {PORTRAITS.map((_, i) => (
            <button key={i} onClick={() => goTo(i)} style={{
              width: i === activeIdx ? 20 : 8, height: 8, borderRadius: 4, border: "none",
              background: i === activeIdx ? p.color : "rgba(255,255,255,0.2)",
              boxShadow: i === activeIdx ? `0 0 8px ${p.color}` : "none",
              cursor: "pointer", transition: "all 0.3s cubic-bezier(0.23,1,0.32,1)", padding: 0,
            }} />
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 8, fontFamily: "\'Rajdhani\', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: 1.5 }}>
          ← SWIPE TO BROWSE AGENTS →
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={() => navigate(`/matchmaker?dept=${p.deptId}&agent=${p.employeeId}`)} style={{
            flex: 1, padding: "11px 0",
            background: `linear-gradient(90deg, ${p.color}22, ${p.color}44, ${p.color}22)`,
            border: `1px solid ${p.color}88`, borderRadius: 6, color: p.color,
            fontFamily: "\'Orbitron\', monospace", fontSize: 9, fontWeight: 700, letterSpacing: 2,
            cursor: "pointer", textTransform: "uppercase", boxShadow: `0 0 20px ${p.color}22`,
          }}>⬡ FIND MY TEAM</button>
          <button onClick={() => openCart()} style={{
            flex: 1, padding: "11px 0",
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 6, color: "rgba(255,255,255,0.8)",
            fontFamily: "\'Orbitron\', monospace", fontSize: 9, fontWeight: 700, letterSpacing: 2,
            cursor: "pointer", textTransform: "uppercase",
          }}>🛒 VIEW CART</button>
        </div>
      </div>
      <style>{`
        @keyframes scanSweepV {
          0% { top: -2px; }
          100% { top: 100%; }
        }
      `}</style>
    </div>
  );
}

interface OnyxGlassPanelProps {}

export default function OnyxGlassPanel({}: OnyxGlassPanelProps) {
  const [activeCard, setActiveCard] = useState(0);
  const [glitching, setGlitching] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [, navigate] = useLocation();

  // Auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      setGlitching(true);
      setTimeout(() => {
        setActiveCard((prev) => (prev + 1) % PORTRAITS.length);
        setGlitching(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const portrait = PORTRAITS[activeCard];

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 260,
        display: "flex",
        flexDirection: "row",
        gap: 0,
        overflow: "hidden",
        // Outer glow matching active agent color
        boxShadow: `0 0 60px ${portrait.color}18`,
        transition: "box-shadow 0.6s ease",
      }}
    >
      {/* ── DIAGONAL PARALLELOGRAM CARDS ── */}
      {PORTRAITS.map((p, i) => {
        const isActive = i === activeCard;
        const isHovered = hoveredCard === i;
        // Active card gets ~40% width, others share the rest equally
        const flexVal = isActive ? 4 : 1;

        return (
          <div
            key={i}
            onClick={() => {
              setGlitching(true);
              setTimeout(() => { setActiveCard(i); setGlitching(false); }, 200);
            }}
            onMouseEnter={() => setHoveredCard(i)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              flex: flexVal,
              position: "relative",
              overflow: "hidden",
              cursor: "pointer",
              // Diagonal parallelogram via skew — negative margin compensates for skew overlap
              transform: "skewX(-8deg)",
              marginLeft: i === 0 ? 0 : -14,
              // Border on right edge only (left is covered by next card)
              borderRight: `1px solid ${isActive ? p.color + "88" : isHovered ? p.color + "44" : "rgba(255,255,255,0.06)"}`,
              borderTop: `1px solid ${isActive ? p.color + "44" : "rgba(255,255,255,0.04)"}`,
              borderBottom: `1px solid ${isActive ? p.color + "44" : "rgba(255,255,255,0.04)"}`,
              boxShadow: isActive
                ? `inset 0 0 40px ${p.color}18, 0 0 30px ${p.color}22`
                : isHovered
                ? `inset 0 0 20px ${p.color}0e`
                : "none",
              transition: "flex 0.45s cubic-bezier(0.23,1,0.32,1), border-color 0.3s, box-shadow 0.3s",
              background: "rgba(4,8,20,0.92)",
              zIndex: isActive ? 2 : isHovered ? 1 : 0,
            }}
          >
            {/* Un-skew the inner content so image/text are straight */}
            <div style={{ transform: "skewX(8deg)", width: "calc(100% + 28px)", marginLeft: -14, height: "100%", position: "relative" }}>
              {/* Portrait image */}
              <img
                src={p.src}
                alt={p.name}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: p.objectPosition ?? "center 8%",
                  display: "block",
                  opacity: isActive ? 1 : isHovered ? 0.75 : 0.35,
                  filter: glitching && isActive
                    ? "hue-rotate(90deg) saturate(3) brightness(1.4)"
                    : isActive
                    ? "brightness(1.05) saturate(1.2)"
                    : "brightness(0.7) saturate(0.8)",
                  transition: "opacity 0.4s ease, filter 0.3s ease",
                }}
              />

              {/* Dark gradient overlay — stronger on inactive cards */}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                background: isActive
                  ? "linear-gradient(to top, rgba(4,8,20,0.9) 0%, rgba(4,8,20,0.3) 40%, transparent 70%)"
                  : "linear-gradient(to top, rgba(4,8,20,0.97) 0%, rgba(4,8,20,0.7) 50%, rgba(4,8,20,0.4) 100%)",
                transition: "background 0.4s ease",
              }} />

              {/* Scan-line texture */}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,245,255,0.015) 2px, rgba(0,245,255,0.015) 4px)",
              }} />

              {/* Animated scan sweep on active */}
              {isActive && (
                <div style={{
                  position: "absolute", left: 0, right: 0, height: 2, zIndex: 3,
                  background: `linear-gradient(90deg, transparent, ${p.color}88, transparent)`,
                  animation: "scanSweepV 3s linear infinite",
                  pointerEvents: "none",
                }} />
              )}

              {/* Color tint on hover */}
              {isHovered && !isActive && (
                <div style={{
                  position: "absolute", inset: 0, pointerEvents: "none",
                  background: `radial-gradient(ellipse at 50% 30%, ${p.color}18 0%, transparent 70%)`,
                }} />
              )}

              {/* Active: dept badge top-left */}
              {isActive && (
                <div style={{
                  position: "absolute", top: 14, left: 20, zIndex: 5,
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: `${p.color}18`, border: `1px solid ${p.color}55`,
                  borderRadius: 4, padding: "3px 10px",
                }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: p.color, boxShadow: `0 0 6px ${p.color}` }} />
                  <span style={{ fontFamily: "'Orbitron', monospace", fontSize: 9, letterSpacing: 2, color: p.color, textTransform: "uppercase" }}>
                    {p.dept}
                  </span>
                </div>
              )}

              {/* Active: corner brackets */}
              {isActive && [
                { top: 8, left: 8, borderTop: `2px solid ${p.color}`, borderLeft: `2px solid ${p.color}` },
                { top: 8, right: 8, borderTop: `2px solid ${p.color}`, borderRight: `2px solid ${p.color}` },
                { bottom: 8, left: 8, borderBottom: `2px solid ${p.color}`, borderLeft: `2px solid ${p.color}` },
                { bottom: 8, right: 8, borderBottom: `2px solid ${p.color}`, borderRight: `2px solid ${p.color}` },
              ].map((s, j) => (
                <div key={j} style={{ position: "absolute", width: 14, height: 14, zIndex: 6, ...s }} />
              ))}

              {/* Bottom info — active card shows full info, inactive shows just name */}
              <div style={{
                position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 5,
                padding: isActive ? "12px 16px 14px" : "8px 10px",
              }}>
                {isActive ? (
                  <>
                    <div style={{ fontFamily: "'Orbitron', monospace", fontSize: 15, fontWeight: 900, color: "#fff", letterSpacing: 2, textShadow: `0 0 20px ${p.color}88` }}>
                      {p.name}
                    </div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 11, color: `${p.color}cc`, letterSpacing: 1.5, textTransform: "uppercase", marginTop: 2 }}>
                      {p.role}
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                      {p.skills.map((skill) => (
                        <span key={skill} style={{
                          fontFamily: "'Rajdhani', sans-serif", fontSize: 9, letterSpacing: 1,
                          color: "rgba(255,255,255,0.65)", background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "2px 6px",
                        }}>{skill}</span>
                      ))}
                    </div>
                    {/* CTA row */}
                    <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/matchmaker?dept=${p.deptId}&agent=${p.employeeId}`); }}
                        style={{
                          flex: 1, padding: "8px 0",
                          background: `linear-gradient(90deg, ${p.color}22, ${p.color}44, ${p.color}22)`,
                          border: `1px solid ${p.color}88`, borderRadius: 4, color: p.color,
                          fontFamily: "'Orbitron', monospace", fontSize: 8, fontWeight: 700, letterSpacing: 1.5,
                          cursor: "pointer", textTransform: "uppercase",
                          boxShadow: `0 0 16px ${p.color}22`,
                        }}
                      >⬡ DEPLOY</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{
                      fontFamily: "'Orbitron', monospace",
                      fontSize: isHovered ? 10 : 8,
                      fontWeight: 700,
                      color: isHovered ? p.color : "rgba(255,255,255,0.4)",
                      letterSpacing: 1.5,
                      textShadow: isHovered ? `0 0 12px ${p.color}` : "none",
                      transition: "all 0.25s ease",
                      writingMode: "horizontal-tb",
                    }}>
                      {p.name}
                    </div>
                    {isHovered && (
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 8, color: `${p.color}99`, letterSpacing: 1, marginTop: 2 }}>
                        {p.role}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Active indicator dot */}
              {isActive && (
                <div style={{
                  position: "absolute", top: 14, right: 16, zIndex: 6,
                  width: 7, height: 7, borderRadius: "50%",
                  background: p.color, boxShadow: `0 0 10px ${p.color}`,
                  animation: "pulse 1.5s ease-in-out infinite",
                }} />
              )}
            </div>
          </div>
        );
      })}

      {/* Glitch overlay on active card */}
      {glitching && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10, pointerEvents: "none",
          background: `linear-gradient(transparent 30%, ${portrait.color}18 50%, transparent 70%)`,
          animation: "glitchFlash 0.3s steps(3) forwards",
        }} />
      )}

      <style>{`
        @keyframes scanSweepV {
          0% { top: -2px; }
          100% { top: 100%; }
        }
        @keyframes glitchFlash {
          0% { opacity: 1; transform: translateX(-2px); }
          33% { opacity: 0.5; transform: translateX(2px); }
          66% { opacity: 1; transform: translateX(-1px); }
          100% { opacity: 0; transform: translateX(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
        .mobile-portrait-carousel { display: none !important; }
        @media (max-width: 767px) {
          .mobile-portrait-carousel { display: block !important; }
        }
      `}</style>
    </div>
  );
}

// Export the mobile carousel separately so CyberHeader can place it below the hero title on mobile
export { MobilePortraitCarousel };
